import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle } from '@mui/material';
import {
    List,
    DataTable,
    NumberField,
    TextField,
    SelectInput,
    TextInput,
    TopToolbar,
    EditButton,
    ExportButton,
    ColumnsButton,
    FilterButton,
    useDataProvider,
    useRecordContext,
} from 'react-admin';
import {
    PLAN_TYPE_CHOICES,
    PLAN_TYPE_MAP,
    SUBSCRIPTION_STATUS_CHOICES,
    SUBSCRIPTION_STATUS_MAP,
} from './constants';
import { UserSubscriptionRecord } from './types';

const SubscriptionListActions = () => (
    <TopToolbar>
        <FilterButton />
        <ColumnsButton />
        <ExportButton />
    </TopToolbar>
);

const PlanTypeField = () => {
    const record = useRecordContext<UserSubscriptionRecord>();
    if (!record) return null;
    const planType = PLAN_TYPE_MAP[record.plan_type] || { label: 'UNKNOWN', color: '#546e7a' };
    return <Chip label={planType.label} size="small" sx={{ backgroundColor: planType.color, color: '#fff' }} />;
};

const SubscriptionStatusField = () => {
    const record = useRecordContext<UserSubscriptionRecord>();
    if (!record) return null;
    const status = SUBSCRIPTION_STATUS_MAP[record.status] || { label: 'UNKNOWN', color: '#546e7a' };
    return <Chip label={status.label} size="small" sx={{ backgroundColor: status.color, color: '#fff' }} />;
};

const PlayerField = ({ choices }: { choices: Array<any> }) => {
    const record = useRecordContext<UserSubscriptionRecord>();
    if (!record) return null;
    const item = choices.find((p: any) => p.id === record.user_id || p.id == record.user_id);
    return <span>{item ? item.name : record.user_id}</span>;
};

type OrderDetail = {
    id: number;
    userId?: number;
    productId?: number;
    title?: string;
    content?: string;
    price?: number;
    kind?: number;
    status?: number;
};

const getOrderLabel = (map: Record<number, string>, value?: number) => {
    if (value === undefined) {
        return '-';
    }
    return map[value] || String(value);
};

const OrderDetailField = ({
    kindMap,
    statusMap,
}: {
    kindMap: Record<number, string>;
    statusMap: Record<number, string>;
}) => {
    const record = useRecordContext<UserSubscriptionRecord>();
    const dataProvider = useDataProvider();
    const [detail, setDetail] = useState<OrderDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let mounted = true;

        if (!record?.order_id || Number(record.order_id) === 0) {
            setDetail(null);
            setLoading(false);
            return () => {
                mounted = false;
            };
        }

        setLoading(true);
        dataProvider
            .getOne('admin/orders', { id: record.order_id })
            .then(({ data }) => {
                if (mounted) {
                    setDetail(data as OrderDetail);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (mounted) {
                    setDetail(null);
                    setLoading(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [dataProvider, record?.order_id]);

    if (!record?.order_id || Number(record.order_id) === 0) {
        return <span>No detail</span>;
    }

    if (loading) {
        return <span>Loading...</span>;
    }

    if (!detail) {
        return <span>Not found</span>;
    }

    const openDialog = (event: React.MouseEvent) => {
        event.stopPropagation();
        setOpen(true);
    };

    const closeDialog = () => {
        setOpen(false);
    };

    const stopRowNavigation = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    return (
        <Box onClick={stopRowNavigation} onMouseDown={stopRowNavigation}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, lineHeight: 1.4 }}>
                <span>{detail.title || `Order #${detail.id}`}</span>
                <span style={{ color: '#666', fontSize: 12 }}>
                    {`$${Number(detail.price || 0).toFixed(2)} | ${getOrderLabel(kindMap, detail.kind)} | ${getOrderLabel(statusMap, detail.status)}`}
                </span>
                <Button size="small" onClick={openDialog}>
                    View detail
                </Button>
            </Box>
            <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{detail.title || `Order #${detail.id}`}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1, columnGap: 2, fontSize: 14 }}>
                        <Box sx={{ fontWeight: 600 }}>Order ID</Box>
                        <Box>{detail.id}</Box>
                        <Box sx={{ fontWeight: 600 }}>User ID</Box>
                        <Box>{detail.userId ?? '-'}</Box>
                        <Box sx={{ fontWeight: 600 }}>Product ID</Box>
                        <Box>{detail.productId ?? '-'}</Box>
                        <Box sx={{ fontWeight: 600 }}>Kind</Box>
                        <Box>{getOrderLabel(kindMap, detail.kind)}</Box>
                        <Box sx={{ fontWeight: 600 }}>Status</Box>
                        <Box>{getOrderLabel(statusMap, detail.status)}</Box>
                        <Box sx={{ fontWeight: 600 }}>Price</Box>
                        <Box>{`$${Number(detail.price || 0).toFixed(2)}`}</Box>
                        <Box sx={{ fontWeight: 600 }}>Title</Box>
                        <Box>{detail.title || '-'}</Box>
                        <Box sx={{ fontWeight: 600 }}>Content</Box>
                        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{detail.content || '-'}</Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

const Column = DataTable.Col<UserSubscriptionRecord>;

const UserSubscriptionList = () => {
    const dataProvider = useDataProvider();
    const [playerChoices, setPlayerChoices] = useState<Array<any>>([]);
    const [orderKindMap, setOrderKindMap] = useState<Record<number, string>>({});
    const [orderStatusMap, setOrderStatusMap] = useState<Record<number, string>>({});

    useEffect(() => {
        let mounted = true;
        Promise.all([
            dataProvider.getList('admin/dropdown/players', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: '', order: 'ASC' },
                filter: {},
            }),
            dataProvider.getList('admin/dropdown/order-kinds', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: '', order: 'ASC' },
                filter: {},
            }),
            dataProvider.getList('admin/dropdown/order-statuses', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: '', order: 'ASC' },
                filter: {},
            }),
        ])
            .then(([players, orderKinds, orderStatuses]) => {
                if (!mounted) {
                    return;
                }

                setPlayerChoices(players.data || []);
                setOrderKindMap(
                    Object.fromEntries((orderKinds.data || []).map((item: { id: number; name: string }) => [Number(item.id), item.name]))
                );
                setOrderStatusMap(
                    Object.fromEntries((orderStatuses.data || []).map((item: { id: number; name: string }) => [Number(item.id), item.name]))
                );
            })
            .catch(() => {});

        return () => {
            mounted = false;
        };
    }, [dataProvider]);

    const subscriptionFilters = [
        <SelectInput key="userId" label="Player" source="userId" choices={playerChoices} alwaysOn />,
        <TextInput key="orderId" label="Order ID" source="orderId" />,
        <SelectInput key="planType" label="Plan" source="planType" choices={PLAN_TYPE_CHOICES} />,
        <SelectInput key="status" label="Status" source="status" choices={SUBSCRIPTION_STATUS_CHOICES} />,
        <TextInput key="subscribedAt" label="Subscribed At (YYYY-MM-DD)" source="subscribedAt" />,
        <TextInput key="periodStartDate" label="Period Start (YYYY-MM-DD)" source="periodStartDate" />,
        <TextInput key="periodEndDate" label="Period End (YYYY-MM-DD)" source="periodEndDate" />,
    ];

    return (
        <List
            filters={subscriptionFilters}
            actions={<SubscriptionListActions />}
            perPage={25}
            sort={{ field: 'id', order: 'DESC' }}
        >
            <DataTable rowClick="edit" bulkActionButtons={false}>
                <Column source="id" label="ID">
                    <NumberField source="id" />
                </Column>
                <Column source="user_id" label="Player Email (Name)">
                    <PlayerField choices={playerChoices} />
                </Column>
                <Column source="order_id" label="Order Detail">
                    <OrderDetailField kindMap={orderKindMap} statusMap={orderStatusMap} />
                </Column>
                <Column source="plan_type" label="Plan">
                    <PlanTypeField />
                </Column>
                <Column source="status" label="Status">
                    <SubscriptionStatusField />
                </Column>
                <Column source="created_at" label="Created At">
                    <TextField source="created_at" />
                </Column>
                <Column source="period_start_date" label="Period Start">
                    <TextField source="period_start_date" />
                </Column>
                <Column source="period_end_date" label="Period End">
                    <TextField source="period_end_date" />
                </Column>
                <Column source="ai_dialog_limit" label="AI Dialog Limit">
                    <NumberField source="ai_dialog_limit" />
                </Column>
                <Column source="ai_dialog_used" label="AI Dialog Used">
                    <NumberField source="ai_dialog_used" />
                </Column>
                <Column>
                    <EditButton />
                </Column>
            </DataTable>
        </List>
    );
};

export default UserSubscriptionList;
